import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  X, Save, Send, Eye, Mail, Users, UserMinus, Plus, Search, Tag,
  AlertTriangle, CheckCircle2, Loader2, FileText, Paperclip, MessageSquare,
} from 'lucide-react';
import apiClient from '../utils/apiClient';

/**
 * CustomEmailTemplateModal — unified Edit + Save + Send surface for
 * operator-authored email templates (kind="custom" rows in
 * template_overrides).
 *
 * Replaces the old "navigate to Communication > Email with handoff via
 * sessionStorage" flow. One modal handles four operator flows:
 *
 *   1. Open template -> tweak subject -> send to one tenant without saving
 *   2. Open template -> tweak body   -> save the change -> send to all
 *   3. Open template -> just edit and save -> close (never send)
 *   4. Open template -> just send without editing (use as-is)
 *
 * Props:
 *   facility     full facility doc (used by composer label only)
 *   facilityId   string, required
 *   template     existing template row, OR null for "new template" mode
 *   onClose()    closes the modal
 *   onSaved(t)   optional, called after successful PUT/POST
 *   onSent(r)    optional, called after a successful bulk-email/send POST
 *
 * Composer patterns (recipient picker, attachments, preview, send) are
 * DUPLICATED from frontend/src/pages/BulkEmail.js intentionally. A future
 * refactor can extract a shared BulkEmailComposer if a third surface
 * needs it; for now duplication is the lower-risk path.
 */

// --- Static config (mirrors BulkEmail.js + CustomTemplatesTab.js) -----------

const FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'delinquent', label: 'Delinquent' },
  { key: 'all', label: 'All' },
  { key: 'archived', label: 'Archived' },
];

const MERGE_TAGS = [
  'tenant_name', 'customer_name', 'first_name', 'customer_email', 'customer_phone',
  'facility_name', 'facility_phone', 'facility_address',
  'unit_number', 'monthly_rate', 'balance', 'due_date', 'start_date', 'end_date',
  'gate_code', 'access_code',
  'pay_url', 'portal_url', 'rent_url', 'signing_url',
  'invoice_number', 'invoice_amount',
];

const ATTACHMENT_SIZE_CAP_BYTES = 8 * 1024 * 1024;

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

const escapeHtml = (s) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Same body-build behavior as BulkEmail: plain mode -> wrap paragraphs
// with inline-styled <p>; html mode -> pass through unchanged.
const buildHtml = (raw, mode) => {
  if (mode === 'html') return raw;
  const paragraphs = (raw || '').split(/\n{2,}/);
  return paragraphs
    .map(
      (p) =>
        `<p style="margin: 0 0 16px 0; line-height: 1.5;">${escapeHtml(p).replace(/\n/g, '<br>')}</p>`,
    )
    .join('\n');
};

// Strip HTML tags + decode entities for the "HTML -> Plain text" toggle so the
// operator sees clean prose in the textarea instead of raw markup. Preserves
// paragraph and line breaks: <p> + </p> -> double newline, <br> -> single
// newline, then strip every remaining tag.
const htmlToPlain = (html) => {
  if (!html) return '';
  let s = String(html);
  // Block-level boundaries -> paragraph breaks
  s = s.replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n\n');
  s = s.replace(/<(br|hr)\s*\/?>/gi, '\n');
  // Strip everything else
  s = s.replace(/<[^>]+>/g, '');
  // Decode the common entities our buildHtml writes back out
  s = s
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
  // Collapse 3+ newlines down to 2 (one blank line between paragraphs)
  s = s.replace(/\n{3,}/g, '\n\n').trim();
  return s;
};

// Encode a File to base64 (no data-URL prefix).
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });


// ============================================================================
// Modal
// ============================================================================

const CustomEmailTemplateModal = ({
  facility,
  facilityId,
  template,
  channel: channelProp,
  onClose,
  onSaved,
  onSent,
}) => {
  const isEdit = !!template;

  // Channel resolution: an existing row carries its own channel (legacy rows
  // with none are email); a brand-new template takes the channel the tab
  // hands in via the prop ("email" / "sms"). Fixed for the modal's lifetime —
  // operators don't switch an email template into a text one mid-edit.
  const channel = template?.channel || channelProp || 'email';
  const isSms = channel === 'sms';

  // 160 chars = one SMS segment; above that the message splits into multi-part
  // SMS (still billed per part). Mirrors the per-customer Text tab counter.
  const SMS_SEGMENT = 160;

  // --- Editor state ----------------------------------------------------------
  const [name, setName] = useState(template?.name || '');
  const [templateKey, setTemplateKey] = useState(template?.template_key || '');
  const [keyTouched, setKeyTouched] = useState(isEdit);
  const [subject, setSubject] = useState(template?.subject || '');
  const [bodyHtml, setBodyHtml] = useState(template?.html_body || '');
  const [bodyText, setBodyText] = useState(template?.sms_body || '');
  // Default to "html" for existing templates (they were authored as HTML);
  // default to "plain" for brand-new templates so casual operators just type.
  const [bodyMode, setBodyMode] = useState(isEdit ? 'html' : 'plain');
  const [enabled, setEnabled] = useState(template?.enabled !== false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);

  // Snapshot to detect unsaved edits. Reset every time we Save successfully.
  const [originalSnapshot, setOriginalSnapshot] = useState(() => ({
    name: template?.name || '',
    subject: template?.subject || '',
    bodyHtml: template?.html_body || '',
    bodyText: template?.sms_body || '',
    enabled: template?.enabled !== false,
    attachmentFilename: template?.attachment_label
      || (template?.attachment_path ? template.attachment_path.split('/').pop() : null)
      || null,
  }));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveInfo, setSaveInfo] = useState(null);

  // --- Recipient picker state (copied pattern from BulkEmail.js) ------------
  const [filterKey, setFilterKey] = useState('active');
  const [search, setSearch] = useState('');
  const [available, setAvailable] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState(null);
  const [pickedId, setPickedId] = useState('');
  const [selected, setSelected] = useState([]);
  const debounceRef = useRef(null);

  // --- Preview / send state -------------------------------------------------
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  // --- Refs for caret-aware merge-tag insertion -----------------------------
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const smsRef = useRef(null);
  const focusedRef = useRef('bodyHtml');

  // --- Unsaved-changes detection --------------------------------------------
  const currentAttachmentFilename = useMemo(
    () => (attachments.length > 0 ? attachments[0].filename : null),
    [attachments],
  );
  const hasUnsavedChanges = useMemo(() => {
    if (!isEdit) {
      // New template: any field with content counts as unsaved.
      return !!(name || subject || bodyHtml || bodyText || attachments.length);
    }
    return (
      name !== originalSnapshot.name
      || subject !== originalSnapshot.subject
      || bodyHtml !== originalSnapshot.bodyHtml
      || bodyText !== originalSnapshot.bodyText
      || enabled !== originalSnapshot.enabled
      || currentAttachmentFilename !== originalSnapshot.attachmentFilename
    );
  }, [
    isEdit, name, subject, bodyHtml, bodyText, enabled,
    currentAttachmentFilename, originalSnapshot, attachments.length,
  ]);

  // --- Pre-load attachment for existing template ----------------------------
  // Hits GET /templates/{key}/attachment which now resolves the inline
  // attachment_blob OR the legacy filesystem path. If the GET 404s (the
  // template was seeded with a filesystem path that doesn't exist on prod),
  // we surface a clear "re-upload" prompt and leave the attachments list
  // empty so the operator can pick a fresh file without a broken pre-fill.
  useEffect(() => {
    const hasInlineBlob = !!template?.attachment_filename || !!template?.attachment_blob;
    const hasLegacyPath = !!template?.attachment_path;
    if (!isEdit || (!hasInlineBlob && !hasLegacyPath)) return;
    const fetchUrl = `/api/facilities/${facilityId}/templates/${template.template_key}/attachment`;
    const filename =
      template.attachment_filename
      || template.attachment_label
      || (template.attachment_path ? template.attachment_path.split('/').pop() : 'attachment');
    let cancelled = false;
    (async () => {
      setAttachmentLoading(true);
      try {
        const res = await apiClient.get(fetchUrl, { responseType: 'blob' });
        const blob = res.data;
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || '';
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        if (cancelled) return;
        setAttachments([{
          filename,
          content_base64: base64,
          content_type: blob.type || 'application/octet-stream',
          size: blob.size,
        }]);
      } catch (e) {
        if (cancelled) return;
        // 404 = attachment file missing on disk (legacy filesystem path
        // that doesn't deploy to prod). Surface a re-upload prompt and
        // leave attachments empty so the file picker is the operator's
        // unambiguous next step.
        const status = e?.response?.status;
        if (status === 404) {
          setAttachmentError(
            'The attachment for this template is no longer available. Please re-upload it below.',
          );
        } else {
          setAttachmentError(
            `Could not load template attachment: ${e?.response?.data?.detail || e.message}`,
          );
        }
        setAttachments([]);
      } finally {
        if (!cancelled) setAttachmentLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Recipient picker fetch (copied from BulkEmail.js) --------------------
  const loadAvailable = useCallback(async (f, q) => {
    if (!facilityId) return;
    setAvailableLoading(true);
    setAvailableError(null);
    try {
      const params = new URLSearchParams();
      params.set('filter', f);
      if (q) params.set('search', q);
      params.set('limit', '500');
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/bulk-email/customers?${params.toString()}`,
      );
      setAvailable(r.data || []);
    } catch (e) {
      setAvailableError(e?.response?.data?.detail || e.message);
      setAvailable([]);
    } finally {
      setAvailableLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { loadAvailable(filterKey, ''); }, [loadAvailable, filterKey]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadAvailable(filterKey, search.trim());
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, filterKey, loadAvailable]);

  // --- Picker helpers -------------------------------------------------------
  const selectedIds = useMemo(() => new Set(selected.map((s) => s._id)), [selected]);
  const availableNotPicked = useMemo(
    () => available.filter((c) => !selectedIds.has(c._id)),
    [available, selectedIds],
  );

  const addOne = (id) => {
    const target = id || pickedId;
    if (!target) return;
    const c = available.find((x) => x._id === target);
    if (!c) return;
    if (selectedIds.has(c._id)) return;
    setSelected((prev) => [...prev, c]);
    setPickedId('');
    setPreview(null);
  };
  // No batching button: the send pipeline paces every email under the
  // provider rate limit (notification_service pacer), so manual chunking
  // added clicks without adding safety. Removed 2026-07-29.
  const addAll = () => {
    if (availableNotPicked.length === 0) return;
    setSelected((prev) => [...prev, ...availableNotPicked]);
    setPickedId('');
    setPreview(null);
  };
  const removeOne = (id) => {
    setSelected((prev) => prev.filter((c) => c._id !== id));
    setPreview(null);
  };
  const removeAll = () => {
    setSelected([]);
    setPreview(null);
  };

  // --- Attachment picker ----------------------------------------------------
  const onAttachmentPick = async (e) => {
    setAttachmentError(null);
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [];
    for (const f of files) {
      if (f.size > ATTACHMENT_SIZE_CAP_BYTES) {
        setAttachmentError(
          `"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)} MB - files must be under 8 MB.`,
        );
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      const content_base64 = await fileToBase64(f);
      next.push({
        filename: f.name,
        content_base64,
        content_type: f.type || null,
        size: f.size,
      });
    }
    setAttachments((prev) => [...prev, ...next]);
    e.target.value = '';
  };
  const removeAttachment = (i) =>
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  // --- Merge tag insertion at caret -----------------------------------------
  const insertTag = (tag) => {
    const token = `{{${tag}}}`;
    const field = focusedRef.current;
    let setter; let value; let ref;
    if (field === 'subject') {
      setter = setSubject; value = subject; ref = subjectRef;
    } else if (field === 'bodyText') {
      setter = setBodyText; value = bodyText; ref = smsRef;
    } else {
      setter = setBodyHtml; value = bodyHtml; ref = bodyRef;
    }
    const node = ref.current;
    if (!node) {
      setter((value || '') + token);
      return;
    }
    const start = node.selectionStart ?? node.value.length;
    const end = node.selectionEnd ?? node.value.length;
    const before = node.value.slice(0, start);
    const after = node.value.slice(end);
    setter(before + token + after);
    requestAnimationFrame(() => {
      node.focus();
      const caret = start + token.length;
      node.setSelectionRange(caret, caret);
    });
  };

  // --- Name -> template_key auto-slug (new templates only) ------------------
  const handleNameChange = (v) => {
    setName(v);
    if (!keyTouched && !isEdit) {
      setTemplateKey(slugify(v));
    }
  };

  // --- Save -----------------------------------------------------------------
  const validateSave = () => {
    if (!name.trim()) return 'Name is required.';
    if (!isEdit) {
      if (!templateKey.trim()) return 'Template key is required.';
      if (!/^[a-z0-9_]+$/.test(templateKey)) {
        return 'Template key may only contain lowercase letters, digits, and underscores.';
      }
    }
    if (isSms) {
      // Text template: plain-text body only — no subject/HTML.
      if (!bodyText.trim()) return 'Message body is required.';
      return null;
    }
    if (!subject.trim()) return 'Subject is required.';
    if (!bodyHtml.trim()) return 'Body is required.';
    return null;
  };

  const handleSave = async () => {
    const v = validateSave();
    if (v) {
      setSaveError(v);
      return;
    }
    setSaveError(null);
    setSaveInfo(null);
    setSaving(true);
    try {
      // The body shape mirrors the spec: html stored is the RAW editor
      // content (plain mode is wrapped at send-time so the operator can
      // continue editing it as plain). This matches existing CustomTemplate
      // semantics where html_body holds whatever the operator typed.
      // SMS template: plain-text body only. The bulk-email subject/HTML and
      // inline attachment fields don't apply — the backend stores sms_body
      // and stamps channel="sms".
      const payload = isSms
        ? {
            name,
            sms_body: bodyText,
            enabled,
          }
        : {
            name,
            subject,
            html_body: bodyHtml,
            sms_body: bodyText || null,
            enabled,
          };
      // If the operator uploaded an attachment (either a fresh file via
      // the picker or the pre-loaded blob), persist the FIRST attachment
      // inline on the template doc via attachment_blob. This eliminates
      // the prod 404 by removing the filesystem dependency entirely.
      // Email-only — SMS templates carry no attachments.
      if (!isSms && attachments.length > 0) {
        const first = attachments[0];
        payload.attachment_blob = first.content_base64;
        payload.attachment_filename = first.filename;
        payload.attachment_content_type = first.content_type || 'application/octet-stream';
      }
      let saved;
      if (isEdit) {
        const res = await apiClient.put(
          `/api/facilities/${facilityId}/templates/${template.template_key}`,
          payload,
        );
        saved = res.data;
      } else {
        const res = await apiClient.post(
          `/api/facilities/${facilityId}/templates/`,
          { ...payload, template_key: templateKey, kind: 'custom', channel },
        );
        saved = res.data;
      }
      // Refresh snapshot so "unsaved changes" resets to false.
      setOriginalSnapshot({
        name,
        subject,
        bodyHtml,
        bodyText,
        enabled,
        attachmentFilename: currentAttachmentFilename,
      });
      setSaveInfo('Saved!');
      setTimeout(() => setSaveInfo(null), 2500);
      if (onSaved) onSaved(saved);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // --- Preview --------------------------------------------------------------
  const onPreview = async () => {
    if (!facilityId) return;
    setPreviewError(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const r = await apiClient.post(`/api/facilities/${facilityId}/bulk-email/preview`, {
        customer_ids: selected.map((c) => c._id),
        subject,
        body_html: buildHtml(bodyHtml, bodyMode),
        body_text: bodyText || null,
      });
      setPreview(r.data);
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || e.message);
    } finally {
      setPreviewing(false);
    }
  };

  // --- Send -----------------------------------------------------------------
  const openConfirm = () => {
    setSendError(null);
    setSendResult(null);
    setConfirmOpen(true);
  };

  const onSend = async () => {
    if (!facilityId) return;
    setSending(true);
    setSendError(null);
    try {
      const r = await apiClient.post(`/api/facilities/${facilityId}/bulk-email/send`, {
        customer_ids: selected.map((c) => c._id),
        subject,
        body_html: buildHtml(bodyHtml, bodyMode),
        body_text: bodyText || null,
        attachments: attachments.length
          ? attachments.map((a) => ({
              filename: a.filename,
              content_base64: a.content_base64,
              content_type: a.content_type,
            }))
          : null,
      });
      setSendResult(r.data);
      setConfirmOpen(false);
      if (onSent) onSent(r.data);
    } catch (e) {
      setSendError(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  // --- Close (with unsaved-changes guard) -----------------------------------
  const handleClose = () => {
    if (hasUnsavedChanges) {
      const ok = window.confirm(
        'You have unsaved template edits. Close without saving?',
      );
      if (!ok) return;
    }
    onClose();
  };

  const recipientCount = selected.length;
  const canPreview = !!subject.trim() && !!bodyHtml.trim() && recipientCount > 0 && !previewing;
  const canSend = canPreview && !sending;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            {isSms ? (
              <MessageSquare className="h-4 w-4 text-slate-500" />
            ) : (
              <FileText className="h-4 w-4 text-slate-500" />
            )}
            <h3 className="text-[14px] font-semibold text-slate-900">
              {(() => {
                const kindLabel = isSms ? 'Text' : 'Email';
                return isEdit
                  ? `Edit ${kindLabel} Template${template?.name ? ` - ${template.name}` : ''}`
                  : `New ${kindLabel} Template`;
              })()}
            </h3>
            {hasUnsavedChanges && (
              <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                Unsaved
              </span>
            )}
          </div>
          <button onClick={handleClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">

          {/* ============================================================ */}
          {/* SECTION 1: Template editor                                   */}
          {/* ============================================================ */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-blue-600" />
              <h4 className="text-[13px] font-semibold text-slate-900">Template details</h4>
              <span className="text-[11px] text-slate-400">
                Saved changes apply to future sends from this template.
              </span>
            </div>

            {saveError && (
              <div className="mb-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {saveError}
              </div>
            )}
            {saveInfo && (
              <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5" /> {saveInfo}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Holiday hours announcement"
                  className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">
                  Template key {isEdit ? '' : '*'}
                  {!isEdit && <span className="text-[10px] text-slate-400 ml-1 font-normal">auto-generated, editable</span>}
                </label>
                <input
                  type="text"
                  value={templateKey}
                  onChange={(e) => { setKeyTouched(true); setTemplateKey(e.target.value); }}
                  disabled={isEdit}
                  placeholder="holiday_hours_announcement"
                  className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] font-mono focus:outline-none focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>
            </div>

            {/* Text (SMS) body — plain text only, with a 160-char segment
                counter mirroring the per-customer Text tab. */}
            {isSms && (
              <div className="mb-3">
                <label className="block text-[12px] font-medium text-slate-700 mb-1">Message *</label>
                <textarea
                  ref={smsRef}
                  value={bodyText}
                  onFocus={() => { focusedRef.current = 'bodyText'; }}
                  onChange={(e) => setBodyText(e.target.value)}
                  rows={5}
                  placeholder={'Hi {{tenant_name}}, this is a quick note from {{facility_name}}.'}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500"
                />
                {(() => {
                  const charCount = bodyText.length;
                  const counterClass =
                    charCount > SMS_SEGMENT * 2 ? 'text-red-600'
                      : charCount > SMS_SEGMENT ? 'text-amber-600'
                        : 'text-slate-400';
                  return (
                    <div className={`mt-1 text-[11px] ${counterClass}`}>
                      {charCount} / {SMS_SEGMENT} char{charCount === 1 ? '' : 's'}
                      {charCount > SMS_SEGMENT && (
                        <span> · message will split into multiple SMS</span>
                      )}
                    </div>
                  );
                })()}
                <p className="mt-1 text-[11px] text-slate-400">
                  Plain text only. Merge tags below substitute at send time.
                </p>
              </div>
            )}

            {/* Email Subject + Body + plain-text fallback + attachments */}
            {!isSms && (
            <div className="mb-3">
              <label className="block text-[12px] font-medium text-slate-700 mb-1">Subject *</label>
              <input
                ref={subjectRef}
                type="text"
                value={subject}
                onFocus={() => { focusedRef.current = 'subject'; }}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject line"
                className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
              />
            </div>
            )}

            {!isSms && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[12px] font-medium text-slate-700">Body *</label>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      // Switching HTML -> Plain: strip the tags from the
                      // textarea so the operator sees clean prose. Idempotent
                      // when bodyHtml is already plain (htmlToPlain becomes
                      // a no-op aside from entity decoding).
                      if (bodyMode !== 'plain') {
                        setBodyHtml(htmlToPlain(bodyHtml));
                      }
                      setBodyMode('plain');
                    }}
                    className={`px-3 py-0.5 text-[11px] font-medium ${
                      bodyMode === 'plain' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Plain text
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Switching Plain -> HTML: wrap the current plain
                      // textarea content into <p> blocks so the operator
                      // immediately sees the HTML they'll be sending and
                      // can tweak it from there.
                      if (bodyMode !== 'html') {
                        setBodyHtml(buildHtml(bodyHtml, 'plain'));
                      }
                      setBodyMode('html');
                    }}
                    className={`px-3 py-0.5 text-[11px] font-medium ${
                      bodyMode === 'html' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    HTML
                  </button>
                </div>
              </div>
              <textarea
                ref={bodyRef}
                value={bodyHtml}
                onFocus={() => { focusedRef.current = 'bodyHtml'; }}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={10}
                placeholder={
                  bodyMode === 'html'
                    ? '<p>Hi {{tenant_name}},</p>\n<p>...</p>'
                    : 'Hi {{tenant_name}},\n\nLine breaks and blank lines render correctly.\n\nThanks,\nYour Storage Team'
                }
                className={`w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500 ${
                  bodyMode === 'html' ? 'font-mono' : ''
                }`}
              />
              <p className="mt-1 text-[11px] text-slate-400">
                {bodyMode === 'plain'
                  ? 'Type like a normal email - line breaks become paragraphs automatically.'
                  : 'Paste raw HTML. Tags pass through unchanged.'}
              </p>
            </div>
            )}

            {!isSms && (
            <details className="mb-3">
              <summary className="cursor-pointer text-[12px] font-medium text-slate-600 hover:text-slate-900">
                Plain-text fallback (optional)
              </summary>
              <textarea
                value={bodyText}
                onFocus={() => { focusedRef.current = 'bodyText'; }}
                onChange={(e) => setBodyText(e.target.value)}
                rows={3}
                placeholder="Plain-text variant for clients that don't render HTML. Leave blank to auto-generate."
                className="mt-2 w-full border border-slate-300 rounded-md px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-blue-500"
              />
            </details>
            )}

            {/* Attachments (email only) */}
            {!isSms && (
            <div className="mb-3">
              <label className="block text-[12px] font-medium text-slate-700 mb-1">
                <Paperclip className="inline-block h-3 w-3 mr-1" />
                Attachments (optional)
              </label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="file"
                  multiple
                  onChange={onAttachmentPick}
                  className="text-[12px] text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                />
                <span className="text-[11px] text-slate-400">Up to 8 MB each.</span>
              </div>
              {attachmentLoading && (
                <div className="text-[12px] text-slate-500 flex items-center gap-1.5 mb-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading saved attachment...
                </div>
              )}
              {attachmentError && (
                <div className="text-[12px] text-rose-600 mb-2">{attachmentError}</div>
              )}
              {attachments.length > 0 && (
                <ul className="space-y-1">
                  {attachments.map((a, i) => (
                    <li key={`${a.filename}-${i}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5">
                      <span className="text-[12px] text-slate-700 truncate">
                        <span className="font-medium">{a.filename}</span>
                        <span className="ml-2 text-slate-400">({(a.size / 1024).toFixed(0)} KB)</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(i)}
                        className="text-[11px] text-slate-400 hover:text-rose-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            )}

            {/* Merge tag chips */}
            <div className="mb-3">
              <p className="text-[12px] font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Tag className="h-3 w-3" /> Merge tags
                <span className="text-[10px] text-slate-400 font-normal">click to insert at cursor</span>
              </p>
              <div className="flex flex-wrap gap-1">
                {MERGE_TAGS.map((t) => (
                  <button
                    key={t}
                    onClick={() => insertTag(t)}
                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 font-mono text-[11px] border border-transparent hover:border-blue-200"
                    type="button"
                  >
                    {`{{${t}}}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-[13px] text-slate-700">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="rounded border-slate-300"
                />
                {isSms ? 'Enabled' : 'Enabled (selectable from bulk send)'}
              </label>
              <button
                onClick={handleSave}
                disabled={saving || !hasUnsavedChanges}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[13px] font-medium"
                type="button"
                title={hasUnsavedChanges ? 'Save changes to this template' : 'No changes to save'}
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </section>

          {/* For text templates the SMS send happens from the customer Text
              tab — this modal only authors/stores the template. Surface a
              short note in place of the email send composer. */}
          {isSms && (
            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] text-slate-600 flex items-start gap-2">
              <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
              <span>
                Save this text template, then send it to a tenant from their
                Text tab.
              </span>
            </div>
          )}

          {/* ============================================================ */}
          {/* SECTION 2: Send composer (email only)                        */}
          {/* ============================================================ */}
          {!isSms && (
          <>
          {/* Horizontal divider */}
          <hr className="border-slate-200" />

          <section>
            <div className="flex items-center gap-2 mb-3">
              <Send className="h-4 w-4 text-blue-600" />
              <h4 className="text-[13px] font-semibold text-slate-900">Send this email</h4>
              <span className="text-[11px] text-slate-400">
                Uses the current editor state - you don't need to save first.
              </span>
            </div>

            {/* Source breadcrumb */}
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-[11px] text-blue-700">
              <Mail className="h-3 w-3" />
              Sending from template: <strong>{name || (isEdit ? template?.name : 'Untitled')}</strong>
              {facility?.name && <span className="text-blue-500"> &middot; {facility.name}</span>}
            </div>

            {/* Recipient picker */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-medium text-slate-700 mb-1">Filter customers</label>
                  <select
                    value={filterKey}
                    onChange={(e) => setFilterKey(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500 bg-white"
                  >
                    {FILTERS.map((f) => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-slate-700 mb-1">Search</label>
                  <div className="relative">
                    <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Name, email, phone, customer #"
                      className="w-full border border-slate-300 rounded-md pl-8 pr-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500 bg-white"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">
                  Select customers to add{' '}
                  <span className="text-slate-400 font-normal">
                    ({availableLoading ? 'loading...' : `${availableNotPicked.length} match${availableNotPicked.length === 1 ? '' : 'es'}`})
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <select
                    value={pickedId}
                    onChange={(e) => setPickedId(e.target.value)}
                    disabled={availableLoading}
                    className="flex-1 min-w-[260px] border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500 disabled:bg-slate-50 bg-white"
                  >
                    <option value="">- pick a customer -</option>
                    {availableNotPicked.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.full_name || '(no name)'}
                        {c.customer_number ? ` - ${c.customer_number}` : ''}
                        {c.email ? ` - ${c.email}` : ' - (no email)'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => addOne()}
                    disabled={!pickedId}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[12px] font-medium"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add
                  </button>
                  <button
                    type="button"
                    onClick={addAll}
                    disabled={availableLoading || availableNotPicked.length === 0}
                    title={`Adds all ${availableNotPicked.length} matching recipients. Sends are automatically paced under the provider rate limit.`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[12px] font-medium"
                  >
                    <Users className="h-3.5 w-3.5" />
                    Add all ({availableNotPicked.length})
                  </button>
                  <button
                    type="button"
                    onClick={removeAll}
                    disabled={selected.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 text-slate-700 rounded-md text-[12px] font-medium"
                  >
                    <UserMinus className="h-3.5 w-3.5" /> Remove all
                  </button>
                </div>
                {availableError && (
                  <div className="mt-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {availableError}
                  </div>
                )}
              </div>

              {/* Selected recipients panel */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[12px] font-medium text-slate-700">
                    Selected recipients ({recipientCount})
                  </label>
                  {recipientCount > 0 && (
                    <button
                      type="button"
                      onClick={removeAll}
                      className="text-[11px] text-slate-500 hover:text-rose-600"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {recipientCount === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-4 text-center text-[12px] text-slate-500">
                    No recipients selected. Use the dropdown above or <em>Add all</em>.
                  </div>
                ) : (
                  <ul className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {selected.map((c) => (
                      <li key={c._id} className="flex items-center justify-between px-3 py-1.5 text-[12px]">
                        <div className="flex-1 min-w-0">
                          <span className="text-slate-900 font-medium">{c.full_name || '(no name)'}</span>
                          {c.customer_number && (
                            <span className="text-slate-400 ml-2">{c.customer_number}</span>
                          )}
                          <span className={`ml-2 ${c.email ? 'text-slate-600' : 'text-amber-600'}`}>
                            {c.email || '(no email - will be skipped)'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeOne(c._id)}
                          className="p-1 text-slate-400 hover:text-rose-600 rounded"
                          aria-label="Remove"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Preview + Send action row */}
            <div className="flex flex-wrap gap-2 pt-3">
              <button
                onClick={onPreview}
                disabled={!canPreview}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[13px] font-medium"
              >
                {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                Preview
              </button>
              <button
                onClick={openConfirm}
                disabled={!canSend}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-md text-[13px] font-medium"
              >
                <Send className="h-3.5 w-3.5" />
                {recipientCount > 0
                  ? `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`
                  : 'Send'}
              </button>
            </div>

            {previewError && (
              <div className="mt-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {previewError}
              </div>
            )}

            {sendResult && (
              <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" />
                <span>
                  Sent to <strong>{sendResult.sent_count}</strong> of <strong>{sendResult.recipient_count}</strong> recipients.
                  {sendResult.failed_count > 0 && <> {sendResult.failed_count} failed (see history).</>}
                </span>
              </div>
            )}

            {/* Preview panel */}
            {preview && preview.sample_recipient && (
              <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                <p className="text-[12px] text-slate-500 mb-1">
                  Sample render for <strong>{preview.sample_recipient.full_name}</strong>
                  {' '}({preview.sample_recipient.email})
                  {preview.sample_recipient.unit_number && <> &middot; Unit {preview.sample_recipient.unit_number}</>}
                </p>
                <div className="text-[11px] text-slate-500 mb-1">Subject</div>
                <div className="text-[13px] text-slate-900 mb-2 px-3 py-1.5 bg-slate-50 rounded border border-slate-200">
                  {preview.sample_subject || <em className="text-slate-400">(empty)</em>}
                </div>
                <div className="text-[11px] text-slate-500 mb-1">Rendered HTML</div>
                <div
                  className="rounded-lg border border-slate-200 bg-white p-4 max-h-96 overflow-y-auto text-[13px] text-slate-800"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: preview.sample_body_html || '' }}
                />
              </div>
            )}
          </section>
          </>
          )}
        </div>

        {/* Confirmation modal (inner) */}
        {confirmOpen && (
          <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-[60] px-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-[15px] font-semibold text-slate-900 mb-2">Send this email?</h3>
              <p className="text-[13px] text-slate-600 mb-4">
                You're about to send to <strong>{recipientCount}</strong> recipient
                {recipientCount === 1 ? '' : 's'}. This action cannot be undone.
              </p>
              {sendError && (
                <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2 mb-3">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {sendError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={sending}
                  className="px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={onSend}
                  disabled={sending}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  {sending ? 'Sending...' : 'Send now'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomEmailTemplateModal;
